import { supabase } from './supabase';

export interface InvestigationPaymentStatus {
  orderId: string;
  module: 'lab' | 'radiology';
  paymentStatus: 'pending' | 'partial' | 'paid';
  invoiceId?: string;
  invoiceTotal?: number;
  invoicePaid?: number;
  isPaid: boolean;
  canReleaseReport: boolean;
}

export interface ReportReleaseResult {
  success: boolean;
  reportId: string;
  releasedAt: string;
  releasedBy: string;
  error?: string;
}

export const paymentVerificationService = {
  async verifyLabOrderPayment(orderId: string): Promise<InvestigationPaymentStatus> {
    const { data: order, error } = await supabase
      .from('lab_orders')
      .select(`
        id,
        payment_status,
        invoice_id,
        invoices!invoice_id (
          id,
          total
        )
      `)
      .eq('id', orderId)
      .single();

    if (error) {
      console.error('Error fetching lab order payment status:', error);
      throw new Error('Failed to verify lab order payment');
    }

    const paymentStatus = order.payment_status || 'pending';
    const isPaid = paymentStatus === 'paid';
    const invoice = order.invoices as any;

    let invoicePaid = 0;
    if (invoice?.id) {
      const { data: payments } = await supabase
        .from('payments')
        .select('amount')
        .eq('invoice_id', invoice.id);
      
      if (payments) {
        invoicePaid = payments.reduce((sum, p) => sum + (p.amount || 0), 0);
      }
    }

    return {
      orderId: order.id,
      module: 'lab',
      paymentStatus,
      invoiceId: order.invoice_id,
      invoiceTotal: invoice?.total,
      invoicePaid,
      isPaid,
      canReleaseReport: isPaid || !order.invoice_id,
    };
  },

  async verifyRadiologyOrderPayment(orderId: string): Promise<InvestigationPaymentStatus> {
    const { data: order, error } = await supabase
      .from('radiology_orders')
      .select(`
        id,
        payment_status,
        invoice_id,
        invoices!invoice_id (
          id,
          total
        )
      `)
      .eq('id', orderId)
      .single();

    if (error) {
      console.error('Error fetching radiology order payment status:', error);
      throw new Error('Failed to verify radiology order payment');
    }

    const paymentStatus = order.payment_status || 'pending';
    const isPaid = paymentStatus === 'paid';
    const invoice = order.invoices as any;

    let invoicePaid = 0;
    if (invoice?.id) {
      const { data: payments } = await supabase
        .from('payments')
        .select('amount')
        .eq('invoice_id', invoice.id);
      
      if (payments) {
        invoicePaid = payments.reduce((sum, p) => sum + (p.amount || 0), 0);
      }
    }

    return {
      orderId: order.id,
      module: 'radiology',
      paymentStatus,
      invoiceId: order.invoice_id,
      invoiceTotal: invoice?.total,
      invoicePaid,
      isPaid,
      canReleaseReport: isPaid || !order.invoice_id,
    };
  },

  async getLabReportHoldStatus(reportId: string): Promise<{
    isOnHold: boolean;
    releasedAt?: string;
    releasedBy?: string;
  }> {
    const { data, error } = await supabase
      .from('lab_reports')
      .select('payment_hold, released_at, released_by')
      .eq('id', reportId)
      .single();

    if (error) {
      console.error('Error fetching lab report hold status:', error);
      return { isOnHold: false };
    }

    return {
      isOnHold: data.payment_hold && !data.released_at,
      releasedAt: data.released_at,
      releasedBy: data.released_by,
    };
  },

  async getRadiologyReportHoldStatus(reportId: string): Promise<{
    isOnHold: boolean;
    releasedAt?: string;
    releasedBy?: string;
  }> {
    const { data, error } = await supabase
      .from('radiology_reports')
      .select('payment_hold, released_at, released_by')
      .eq('id', reportId)
      .single();

    if (error) {
      console.error('Error fetching radiology report hold status:', error);
      return { isOnHold: false };
    }

    return {
      isOnHold: data.payment_hold && !data.released_at,
      releasedAt: data.released_at,
      releasedBy: data.released_by,
    };
  },

  async releaseLabReport(reportId: string, userId: string): Promise<ReportReleaseResult> {
    const { data, error } = await supabase
      .from('lab_reports')
      .update({
        released_at: new Date().toISOString(),
        released_by: userId,
      })
      .eq('id', reportId)
      .select()
      .single();

    if (error) {
      console.error('Error releasing lab report:', error);
      return {
        success: false,
        reportId,
        releasedAt: '',
        releasedBy: '',
        error: 'Failed to release lab report',
      };
    }

    return {
      success: true,
      reportId: data.id,
      releasedAt: data.released_at,
      releasedBy: data.released_by,
    };
  },

  async releaseRadiologyReport(reportId: string, userId: string): Promise<ReportReleaseResult> {
    const { data, error } = await supabase
      .from('radiology_reports')
      .update({
        released_at: new Date().toISOString(),
        released_by: userId,
      })
      .eq('id', reportId)
      .select()
      .single();

    if (error) {
      console.error('Error releasing radiology report:', error);
      return {
        success: false,
        reportId,
        releasedAt: '',
        releasedBy: '',
        error: 'Failed to release radiology report',
      };
    }

    return {
      success: true,
      reportId: data.id,
      releasedAt: data.released_at,
      releasedBy: data.released_by,
    };
  },

  async getUnpaidLabOrders(): Promise<any[]> {
    const { data, error } = await supabase
      .from('lab_orders_payment_pending')
      .select('*');

    if (error) {
      console.error('Error fetching unpaid lab orders:', error);
      return [];
    }

    return data || [];
  },

  async getUnpaidRadiologyOrders(): Promise<any[]> {
    const { data, error } = await supabase
      .from('radiology_orders_payment_pending')
      .select('*');

    if (error) {
      console.error('Error fetching unpaid radiology orders:', error);
      return [];
    }

    return data || [];
  },
};
